import { Immutable } from "immer";
import React from "react";
import { slugify } from "tsi-common-react/src/utils/text";
import { t } from "ttag";

import type { StearnsFAQsBlock as Value } from "@reactivated";

type Props = Immutable<{
    value: Value;
}>;

/**
 * One FAQ row, owning its own accordion disclosure state. The
 * `.finance-faq__question.accordion` button toggles `accordion--is-active` and
 * `aria-expanded`, and its `.finance-faq__answer.accordion-target` panel toggles
 * `accordion-target--is-active` -- the modifier that drives the panel open
 * (`display: flex` / `max-height`) in _financing-page.scss. Both the question
 * and answer are raw CMS HTML, injected via `dangerouslySetInnerHTML`.
 */
const FAQItem = (
    props: Immutable<{
        question: string;
        answer: string;
        targetID: string;
        hidden: boolean;
    }>,
): React.JSX.Element => {
    const [isActive, setIsActive] = React.useState(false);
    return (
        <article
            className={
                props.hidden ? "finance-faq finance-faq--hidden" : "finance-faq"
            }
        >
            <h4>
                <button
                    className={`finance-faq__question accordion${
                        isActive ? " accordion--is-active" : ""
                    }`}
                    aria-controls={props.targetID}
                    aria-expanded={isActive ? "true" : "false"}
                    onClick={(event) => {
                        event.preventDefault();
                        setIsActive((active) => !active);
                    }}
                    dangerouslySetInnerHTML={{ __html: props.question }}
                />
            </h4>
            <div
                className={`finance-faq__answer accordion-target${
                    isActive ? " accordion-target--is-active" : ""
                }`}
                id={props.targetID}
                dangerouslySetInnerHTML={{ __html: props.answer }}
            />
        </article>
    );
};

/**
 * Faithful port of the legacy server-rendered `faq.html`. Interactivity is
 * owned here: each FAQ owns its own accordion disclosure state (see FAQItem),
 * and the "Show All FAQs..." reveal is owned by block-level `showAll` state --
 * it drops `finance-faq--hidden` from the overflow items and hides the see-more
 * button with `finance-faq__see-more--hidden`.
 *
 * The block renders as a bare fragment (no wrapping element), matching the
 * legacy `{% for %}`. `counter` is 1-indexed; items 6+ (counter > 5) start
 * hidden, and the see-more button is emitted once, right after the article
 * whose counter == 6.
 */
export const StearnsFAQsBlock = (props: Props): React.JSX.Element => {
    const faqs = props.value.faqs ?? [];
    const [showAll, setShowAll] = React.useState(false);
    return (
        <>
            {faqs.map((item, idx) => {
                const counter = idx + 1;
                const question = item.question ?? "";
                const answer = item.answer ?? "";
                // Reproduce Django `slugify(block.question)`. The question may
                // contain HTML, so strip tags first (Django renders it raw via
                // `|safe`, but slugify operates on the model value); the result
                // is not pixel-visible — it only needs to keep the
                // `aria-controls`/`id` pair linked. Compute once and reuse for
                // both so the accordion association holds.
                const slug = slugify(question.replace(/<[^>]*>/g, " "));
                const targetID = `faq-${slug}-${counter}`;
                return (
                    // Index-derived key: ListBlock items surface no stable id, so
                    // a CMS reorder reuses positional DOM — matches the legacy
                    // `forloop`-positional render.
                    <React.Fragment key={idx}>
                        <FAQItem
                            question={question}
                            answer={answer}
                            targetID={targetID}
                            hidden={counter > 5 && !showAll}
                        />
                        {counter === 6 && (
                            <button
                                className={`finance-faq__see-more al-financing-page__show-all-faqs${
                                    showAll
                                        ? " finance-faq__see-more--hidden"
                                        : ""
                                }`}
                                onClick={() => setShowAll(true)}
                            >
                                {t`Show All FAQs...`}
                            </button>
                        )}
                    </React.Fragment>
                );
            })}
        </>
    );
};
